import { Quote } from "lucide-react";

export interface FeedbackItem {
  firstName: string;
  role: string;
  quote: string;
}

export const FEEDBACK: Record<string, FeedbackItem> = {
  nikhil: {
    firstName: "Nikhil",
    role: "Cybersecurity & Privacy Leader",
    quote:
      "Most gaps only surface during audits. A quick readiness check like this helps shift teams from reactive fixes to proactive ISMS maturity.",
  },
  felicitas: {
    firstName: "Felicitas",
    role: "GRC / Audit Consultant",
    quote:
      "Audit readiness is often scattered across files and teams. Centralized evidence tracking with clear ownership transforms preparation from reactive scrambling into structured progress.",
  },
  david: {
    firstName: "David",
    role: "CISO & Cyber Risk Executive",
    quote:
      "One of the biggest shifts in risk management happens when the register stops being a repository and becomes a decision tool.",
  },
  awuley: {
    firstName: "Awuley",
    role: "Enterprise Management & Business Consultant",
    quote:
      "Risk registers often become audit artifacts instead of living risk management tools. The focus on linking risks to business processes is exactly what many organisations need.",
  },
  sayed: {
    firstName: "Sayed",
    role: "Senior Technical Services & Management Consultant",
    quote:
      "A useful audit reference for showing which applicable security controls have been implemented.",
  },
};

export const FEEDBACK_NOTE =
  "Comments have been lightly edited for clarity and attributed using first name and broad professional role. They are practitioner feedback, not formal customer testimonials.";

interface PractitionerFeedbackProps {
  heading?: string;
  intro?: string;
  keys: Array<keyof typeof FEEDBACK>;
  compact?: boolean;
}

export function PractitionerFeedback({
  heading = "What ISO 27001 and GRC practitioners are saying",
  intro,
  keys,
  compact = false,
}: PractitionerFeedbackProps) {
  const items = keys.map((k) => FEEDBACK[k]).filter(Boolean);
  if (items.length === 0) return null;

  return (
    <section className={`${compact ? "py-14" : "py-16 sm:py-20"} bg-muted/20 border-y border-border`}>
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">{heading}</h2>
          {intro && (
            <p className="text-muted-foreground max-w-3xl mx-auto text-sm sm:text-base">{intro}</p>
          )}
        </div>

        <div
          className={`grid gap-4 items-stretch ${
            items.length >= 3 ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2"
          }`}
        >
          {items.map((f) => (
            <figure
              key={f.firstName + f.role}
              className="bg-card border border-border rounded-2xl p-6 flex flex-col h-full hover:border-accent/40 transition-colors"
            >
              <Quote className="h-5 w-5 text-accent mb-3" aria-hidden="true" />
              <blockquote className="text-sm sm:text-[0.95rem] text-foreground leading-relaxed flex-1">
                "{f.quote}"
              </blockquote>
              <figcaption className="mt-5 pt-4 border-t border-border">
                <div className="text-sm font-semibold text-foreground">{f.firstName}</div>
                <div className="text-xs text-muted-foreground">{f.role}</div>
              </figcaption>
            </figure>
          ))}
        </div>

        <p className="text-xs text-muted-foreground text-center max-w-3xl mx-auto mt-6 italic">
          {FEEDBACK_NOTE}
        </p>
      </div>
    </section>
  );
}